import { Injectable, Optional } from '@angular/core';
import { Observable, of, zip } from 'rxjs';
import { HttpService, LanguageService, SessionService } from '@ecp-caf/caf-common';
import { IframeLink } from '../../models/iframe.model';
import { map, switchMap } from 'rxjs/operators';
import { FrmEventBus } from '../../eventbus/frm-evenbus';
import { EventUtil, FrmUtil, AppType, FuncEventArgs, AppOptions, FuncStateParam } from '../../shared/frm-util';
import { FrameworkVariableService } from '../frmVariableService/framework-variable.service';
import { FrmI18nSettingService } from '../i18nService/i18n-setting.service';


@Injectable()
export class AppService {
    private FUNCSTATUS_PATH = '/api/runtime/sys/v1.0/function-states';
    private APPINVOKE_CAHCE_KEY = 'allappinvoks';
    private frmVariableService: FrameworkVariableService;

    constructor(
        private http: HttpService,
        private sessionSvc: SessionService,
        @Optional() private i18nSetting: FrmI18nSettingService
    ) {
        this.frmVariableService = new FrameworkVariableService();
        if (!this.i18nSetting) {
            const variables = new FrameworkVariableService();
            const language = new LanguageService(null);
            this.i18nSetting = new FrmI18nSettingService(variables, language, http);
        }
    }

    /**
     * App跳转打开
     */
    public openApp(appId: string, appEntrance: string, params: Map<string, string>, reload?: boolean) {
        const appOpts: AppOptions = {
            funcId: '',
            appId,
            appEntrance,
            appType: AppType.App,
            entityParams: params,
            queryStringParams: params,
            isReload: reload
        };
        this.openAppByOptions(appOpts);
    }

    /**
     * App跳转打开服务
     */
    public openAppByOptions(options: AppOptions) {

        const linkobj = this.checkingRepeatOpen(options);
        if (linkobj) {
            return;
        }
        this.getInvokeAndEnterApp$(options).subscribe(
            link => {
                this.getFeb().post(EventUtil.FARRIS_APP_CLICK, link);
            }
        );
    }

    /**
     * 流方式，App跳转打开服务
     */
    public openAppByOptions$(options: AppOptions): Observable<any> {
        const linkobj = this.checkingRepeatOpen(options);
        if (linkobj) {
            return of(true);
        }
        return this.getInvokeAndEnterApp$(options).pipe(
            map(
                link => {
                    this.getFeb().post(EventUtil.FARRIS_APP_CLICK, link);
                    return true;
                }
            )
        );
    }

    /**
     * 获取app调用信息
     */
    public getAppInvok(appId: string): Observable<any> {
        const invoks = localStorage.getItem(appId); //this.getInvokCache();
        //const funcInvok = invoks.find(f => f.id === appId);
        if (!invoks && invoks !== 'null') {
            return this.getInvokConfig(appId).pipe(
                map(v => {
                    if (!v) {
                        return null;
                    }
                    localStorage.setItem(appId, JSON.stringify(v));
                    //this.refreshInvokCache(invoks, v);
                    return v;
                })
            );
        }
        return of(JSON.parse(invoks));
    }

    public beforeCloseAppByOptions(options: AppOptions) {
        const appObkey = EventUtil.appObsToken(options.appId, options.appEntrance, options.tabId);
        const obs = EventUtil.getObserver(EventUtil.BEFORE_FUNC_CLOSE).get(appObkey);
        if (!obs) {
            this.closeAppByOptions(options).subscribe();
            return;
        }

        const eventArgs: FuncEventArgs = {
            tabId: this.getPageId(options.appId, options.appEntrance, options.tabId),
            appType: AppType.App,
            appId: options.appId,
            appEntrance: options.appEntrance
        };
        this.getFeb().post(EventUtil.BEFORE_FUNC_CLOSE, eventArgs);
    }

    /**
     * App关闭前接口
     */
    public beforeCloseApp(appId: string, appEntrance: string, tabId?: string) {
        const self = this;
        const appObkey = EventUtil.appObsToken(appId, appEntrance, tabId);
        const obs = EventUtil.getObserver(EventUtil.BEFORE_FUNC_CLOSE).get(appObkey);
        if (!obs) {
            this.closeApp(appId, appEntrance, tabId).subscribe();
            return;
        }

        const eventArgs: FuncEventArgs = {
            tabId: self.getPageId(appId, appEntrance, tabId),
            appType: AppType.App,
            appId,
            appEntrance
        };
        this.getFeb().post(EventUtil.BEFORE_FUNC_CLOSE, eventArgs);
    }

    public closeAppByOptions(options: AppOptions): Observable<any> {
        const appObkey = EventUtil.appObsToken(options.appId, options.appEntrance, options.tabId);
        const pageToken = this.getPageId(options.appId, options.appEntrance, options.tabId);
        EventUtil.clearObserver(appObkey);
        EventUtil.clearEventPipeWithObkey(pageToken, appObkey);
        EventUtil.clearEventPipe(pageToken);
        localStorage.removeItem(options.appId);
        const objIndex = FrmUtil.getTabArray().findIndex(i => i.id === pageToken && i.appType === AppType.App);
        const obj = FrmUtil.getTabArray()[objIndex];

        options.token = options.token || obj.formToken;
        FrmUtil.removeTab(objIndex);
        const eventArgs: FuncEventArgs = {
            tabId: pageToken,
            appType: AppType.App,
            appId: options.tabId,
            appEntrance: options.appEntrance
        };
        this.getFeb().post(EventUtil.FUNC_CLOSED, eventArgs);
        return this.quitApp(options, obj.su);
    }

    /**
     * 关闭app
     */
    public closeApp(appId: string, appEntrance: string, tabId?: string): Observable<any> {
        const self = this;
        const appObkey = EventUtil.appObsToken(appId, appEntrance, tabId);
        const pageId = this.getPageId(appId, appEntrance, tabId);
        EventUtil.clearObserver(appObkey);
        EventUtil.clearEventPipeWithObkey(pageId, appObkey);
        EventUtil.clearEventPipe(pageId);
        localStorage.removeItem(appId);
        const objIndex = FrmUtil.getTabArray().findIndex(i => i.id === pageId && i.appType === AppType.App);
        const obj = FrmUtil.getTabArray()[objIndex];
        const formToken = obj.formToken;
        FrmUtil.removeTab(objIndex);
        const eventArgs: FuncEventArgs = {
            tabId: pageId,
            appType: AppType.App,
            appId,
            appEntrance,
        };
        self.getFeb().post(EventUtil.FUNC_CLOSED, eventArgs);

        const options: AppOptions = {
            tabId,
            funcId: '',
            appId,
            appEntrance,
            appType: AppType.App,
            token: formToken
        };
        return this.quitApp(options, obj.su).pipe(
            map(v => { })
        );
    }

    private getInvokeAndEnterApp$(options: AppOptions): Observable<IframeLink> {
        const newPageId = this.newPageId(options.appId, options.appEntrance, options.tabId);
        options.tabId = newPageId;
        return this.getAppInvok(options.appId).pipe(
            switchMap(
                value => {
                    let invokEntry = value.appInvoks.find(invok => invok.appEntrance === options.appEntrance);
                    if (!options.appEntrance && !invokEntry) {
                        invokEntry = value.appInvoks.find(invok => !invok.appEntrance);
                    }
                    if (!invokEntry) {
                        window.alert(`请检查当前应用(ID:${options.appId})配置的应用入口(appEntrance:${options.appEntrance})是否存在`);
                        return;
                    }
                    let entityParam: any;
                    try {
                        entityParam = invokEntry.entityParams ? JSON.parse(invokEntry.entityParams) : null;
                    } catch (e) {
                        entityParam = null;
                        // console.log(e);
                    }
                    const su = this.getSu(value.url);

                    return this.enterApp({
                        tabId: newPageId,
                        appId: options.appId,
                        appEntrance: options.appEntrance,
                        menuName: options.tabName || invokEntry.name,
                        action: 'enterapp'
                    }, su).pipe(
                        map(
                            token => {
                                const routurl = value.url + (options.appEntrance ? `#/${options.appEntrance}` : '');
                                const linkobj = new IframeLink(
                                    value.code,
                                    null,
                                    this.buildQueryString(routurl, options.appId, options.appEntrance,
                                        options.tabId, token, options.queryStringParams),
                                );
                                // id加入口区分,tabId页签唯一标识
                                linkobj.id = newPageId;
                                linkobj.appType = AppType.App;
                                linkobj.appId = options.appId;
                                linkobj.appEntrance = options.appEntrance;
                                // 显示app名字及入口名字
                                linkobj.FuncName = options.tabName || invokEntry.name;
                                linkobj.url = value.url;
                                linkobj.reload = options.isReload;
                                linkobj.su = su;
                                linkobj.formToken = token;
                                if (entityParam) {
                                    entityParam.forEach(
                                        item => {
                                            if (item.name === 'mode') {
                                                linkobj.mode = item.value;
                                                linkobj.src += (item.value ? `&appMode=${item.value}&` : '');
                                            }
                                        }
                                    );
                                }

                                if (options.entityParams && options.entityParams.menuSwitchControl) {
                                    linkobj.switchControl = options.entityParams.menuSwitchControl;
                                }

                                this.getFeb().post(newPageId, options.entityParams);
                                return linkobj;
                            }
                        ),
                        switchMap(
                            formLink => {
                                return this.i18nSetting.getSetting$().pipe(
                                    map(
                                        () => formLink
                                    )
                                )
                            }
                        )
                    );
                }
            )
        );
    }

    private getInvokConfig(appId: string) {
        const url = `/api/runtime/sys/v1.0/gspapp/${appId}`;
        return this.http.request('Get', url, FrmUtil.setHeader(null, {}));
    }

    /**
     * 进入应用，触发相关服务
     */
    private enterApp(enterParam: FuncStateParam, su: string): Observable<any> {
        const bizContextUrl = this.FUNCSTATUS_PATH + (!su ? '' : `?su=${su}`);
        return this.http.request('Post', bizContextUrl, FrmUtil.setHeader(null, { body: enterParam })).pipe(
            map((v: any) => {
                return v.token || v.sessionId;
            })
        );
    }

    /**
     * 退出应用，触发相关服务
     */
    private quitApp(options: AppOptions, su?: string): Observable<any> {
        const body = {
            tabId: options.tabId,
            token: options.token,
            appId: options.appId,
            appEntrance: options.appEntrance,
            action: 'quitapp'
        };

        const invokObs = this.getAppInvok(options.appId);
        const bizContextUrl = this.FUNCSTATUS_PATH + (!su ? '' : `?su=${su}`);
        const funcState$ = this.http.request('Post', bizContextUrl, FrmUtil.setHeader(null, { body }));
        // tslint:disable-next-line: deprecation
        const $r = zip(invokObs, funcState$, (res1, res2) => ({ res1, res2 }));
        return $r;
    }

    private getSu(url: string): string {
        const uriArr = url.split('/');
        return uriArr.length > 3 ? uriArr[3] : '';
    }

    private getInvokCache() {
        const appInvokesCache = this.frmVariableService.getFrmVariableByKey(this.APPINVOKE_CAHCE_KEY);
        return appInvokesCache as any[] || [];
    }

    private refreshInvokCache(invoks: any[], funcInvok: any) {
        if (!funcInvok) {
            return;
        }
        invoks.push(funcInvok);
        this.frmVariableService.frameworkVariableReg(this.APPINVOKE_CAHCE_KEY, invoks);
    }

    // tslint:disable-next-line: max-line-length
    private buildQueryString(routurl: string, appId: string, appEntrance: string, tabId: string, token: string, params: Map<string, string>): string {
        if (routurl.indexOf('?') < 0) {
            routurl += `?appId=${appId}&appEntrance=${appEntrance}`;
        } else {
            routurl += `&appId=${appId}&appEntrance=${appEntrance}`;
        }

        routurl += `&appType=${AppType.App}`;
        routurl += `&tabId=${this.getPageId(appId, appEntrance, tabId)}`;
        routurl += !token ? '' : `&cvft=${token}`;
        if (params) {
            params.forEach((value, key) => {
                routurl += !key ? '' : `&${key}=${value}`;
            });
        }
        return routurl;
    }


    private getFeb(): FrmEventBus {
        return EventUtil.getFrmEvent();
    }

    private mergeAppId(appId: string, appEntrance): string {
        return `${appId}_${appEntrance}`;
    }

    /**
     * 获取当前菜单页面的唯一标识
     */
    private getPageId(appId: string, appEntrance: string, tabId: string): string {
        return tabId || this.mergeAppId(appId, appEntrance);
    }

    /**
     * 生成页面唯一标识
     */
    private newPageId(appId: string, appEntrance: string, tabId: string): string {
        return tabId ? `${this.mergeAppId(appId, appEntrance)}_${tabId}` : this.mergeAppId(appId, appEntrance);
    }

    private checkingRepeatOpen(options: AppOptions): IframeLink {
        const appId = options.appId;
        const appEntrance = options.appEntrance;
        const tabId = options.tabId;
        const entityParams = options.entityParams;

        let linkobj: IframeLink;
        const pageId = this.getPageId(appId, appEntrance, tabId);
        linkobj = FrmUtil.getTabArray().find(i => i.id === pageId && i.appType === AppType.App);
        if (linkobj) {
            linkobj.FuncName = options.tabName || linkobj.FuncName;
            linkobj.src = this.buildQueryString(linkobj.url, appId, appEntrance, options.tabId,
                linkobj.sessionid, options.queryStringParams);
            linkobj.reload = options.isReload;
            this.getFeb().post(pageId, entityParams);
            this.getFeb().post(EventUtil.FARRIS_APP_CLICK, linkobj);
        }
        return linkobj;
    }

}
